/**
 * Per-host config for integration types: icon (ApplicationIcon logo key),
 * subtitle (form header subtitle), optional description fallback, and which
 * form component to render. The correct map is selected at runtime via
 * `isIspmHost()`.
 */

export type FormKind =
  | "AWS"
  | "GCPVertex"
  | "Agentforce"
  | "CopilotStudio"
  | "ServiceNow"
  | "AzureFoundry"
  | "AzurePlatform"
  | "GooglePlatform"
  | "AWSPlatform"
  | "NowAssist";

export interface IntegrationTypeMeta {
  /** ApplicationIcon logo shown on the integration card. */
  icon: string;
  /** ApplicationIcon logo shown in the form modal header. Defaults to `icon`. */
  formIcon?: string;
  subtitle: string;
  description?: string;
  form: FormKind;
}

export function isIspmHost(): boolean {
  return (
    typeof window !== "undefined" &&
    (window as Window & { __MFE_HOST__?: string }).__MFE_HOST__ === "ispm"
  );
}

const LEGACY_HOST_TYPES: Record<string, IntegrationTypeMeta> = {
  AWS: {
    icon: "AWS",
    subtitle: "Amazon Web Services",
    form: "AWS",
  },
  AWSPosture: {
    icon: "AWS",
    subtitle: "Amazon Web Services",
    form: "AWS",
  },
  AWSPlatform: {
    icon: "AWS",
    subtitle: "AWS - AI agents and NHI",
    form: "AWS",
  },
  GCPVertex: {
    icon: "GCP",
    subtitle: "Google Vertex",
    form: "GCPVertex",
  },
  GCPVertexAI: {
    icon: "GCP",
    subtitle: "Google Vertex",
    form: "GCPVertex",
  },
  GOOGLE_VERTEX_DISCOVERY: {
    icon: "GCP",
    subtitle: "Google Vertex",
    form: "GCPVertex",
  },
  GooglePlatform: {
    icon: "GCP",
    subtitle: "Google Gemini Agent Platform",
    form: "GCPVertex",
  },
  Agentforce: {
    icon: "Salesforce",
    subtitle: "Salesforce Agentforce",
    form: "Agentforce",
  },
  CopilotStudio: {
    icon: "MicrosoftCopilotStudio",
    formIcon: "MSOffice365",
    subtitle: "Microsoft Copilot Studio",
    form: "CopilotStudio",
  },
  ServiceNowAI: {
    icon: "ServiceNow",
    subtitle: "ServiceNow AI Agent Studio",
    form: "ServiceNow",
  },
  AzureFoundry: {
    icon: "MicrosoftFoundry",
    formIcon: "Azure",
    subtitle: "Azure AI Foundry",
    form: "AzureFoundry",
  },
  EntraID: {
    icon: "EntraID",
    subtitle: "EntraID",
    form: "AWS",
  },
  NowAssist: {
    icon: "ServiceNow",
    subtitle: "Now Assist",
    form: "ServiceNow",
  },
  AzurePlatform: {
    icon: "Azure",
    subtitle: "Microsoft Azure - AI agents and NHI",
    form: "AzureFoundry",
  },
};

const ISPM_HOST_TYPES: Record<string, IntegrationTypeMeta> = {
  AWS: {
    icon: "AWS",
    subtitle: "Amazon Web Services",
    form: "AWS",
  },
  AWSPlatform: {
    icon: "AWS",
    subtitle: "AWS - AI agents and NHI",
    form: "AWS",
  },
  Agentforce: {
    icon: "Salesforce",
    subtitle: "Salesforce Agentforce",
    description:
      "Discover and assess the security posture of AI agents across your Salesforce platform.",
    form: "Agentforce",
  },
  AzurePlatform: {
    icon: "Azure",
    subtitle: "Microsoft Azure - AI agents and NHI",
    form: "AzureFoundry",
  },
  CopilotStudio: {
    icon: "MicrosoftCopilotStudio",
    formIcon: "MSOffice365",
    subtitle: "Microsoft Copilot Studio",
    description:
      "Discover and assess the security posture of AI agents across your Microsoft Power Platform.",
    form: "CopilotStudio",
  },
  GooglePlatform: {
    icon: "GCP",
    subtitle: "Google Gemini Agent Platform",
    form: "GCPVertex",
  },
  NowAssist: {
    icon: "ServiceNow",
    subtitle: "Now Assist",
    form: "ServiceNow",
  },
};

export function getIntegrationTypeMeta(
  typeName: string | null | undefined,
): IntegrationTypeMeta | undefined {
  if (!typeName) return undefined;
  const map = isIspmHost() ? ISPM_HOST_TYPES : LEGACY_HOST_TYPES;
  return map[typeName];
}
